/**
 * ISP-0000 — geometry contract for the Froala field-toolbar emoji button.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The emoji button is the only icon in the field toolbar that is NOT a Froala
 * SVG icon. ISP-2712 replaced a raw "\u{1F60A}" glyph with the app's line-art
 * smiley asset, but injected it through Froala's `template: "text"` path, which
 * wraps the icon in `<span style="text-align:center">`. Froala's own core CSS
 * gives that span `line-height: 17px; padding: 0 4px; overflow: hidden`, and
 * sizes a text-template button to its text width rather than to the 24px icon
 * grid. MEASURED live on web-stg (main.6b6512df.js, 2026-07-31, 1440px,
 * /builder/3169), Wheel Name and Subtitle toolbars, identical on both:
 *
 *   emoticons button        46.00 x 28   <- 22px wider than its siblings
 *   specialCharacters       24.00 x 28
 *   aiAssistant             24.00 x 28
 *   emoticons inner span    46.00 x 17   overflow:hidden, line-height:17px
 *   emoticons <img>         24 x 16      renders 7px BELOW the span's clip box
 *   icon-to-icon gaps       17px, then 6px
 *
 * Two user-visible defects fall out of that:
 *   1. CLIPPING — the image sits on a middle baseline inside a 17px
 *      `overflow: hidden` line box, so its bottom ~7 of 16px is cropped and the
 *      smiley renders as a half circle.
 *   2. UNEVEN GAPS — box-to-box gaps are a uniform 6px, but the emoji's ink sits
 *      inset inside a 46px button while its neighbours fill their 24px buttons
 *      edge to edge, so the *visual* emoji -> omega gap measured 17px against
 *      6px for omega -> sparkle.
 *
 * Both are one root cause: the emoji button never joins the 24px icon grid that
 * `index.scss`'s `button.fr-command.fr-btn > svg { width: 24px; height: 24px }`
 * rule imposes on every SVG icon. This module states that grid explicitly for
 * the one button that cannot inherit it, so the contract is in a single place
 * and is assertable by a test.
 *
 * ROUND 2 — THE BOX IS NOT THE INK (Gordon, 2nd report, screenshot 2026-07-31:
 * "the emoji icon is larger than the other icons"). The round-1 fix above
 * matched the BUTTON BOXES and never compared the glyph ink inside them. It
 * used ONE constant for two different things — the button box and the icon
 * drawn in it — and so sized the emoji image to fill its whole 24px button
 * while a Froala SVG, also 24x24 as an element, paints a much smaller path
 * inside its own viewBox. Containers matched perfectly; the visible ink did
 * not, which is what a user actually looks at.
 *
 * MEASURED live on web-stg (main.d9c2cc1e.js, 2026-07-31, 1440px,
 * /builder/3169), Wheel Name and Subtitle toolbars, identical on both. Ink is
 * the painted extent, from `SVGGraphicsElement.getBBox()` scaled by the
 * element's rendered size over its viewBox — cross-checked against a
 * device-pixel bounding box of a 4x screenshot, agreeing within 0.5px:
 *
 *   element box            ink (painted)
 *   emoticons   24 x 24    22.62 x 22.62   <- 61% taller than the omega's ink
 *   specialChars 24 x 24   13.17 x 14.01
 *   aiAssistant  24 x 24   17.58 x 16.00
 *
 * So the BOX and the GLYPH are deliberately SEPARATE numbers, and must stay
 * separate: the box keeps the icon on the row's grid (that is what makes the
 * gaps even, and it is what round 1 correctly fixed), while
 * `EMOJI_TOOLBAR_GLYPH_PX` sizes the ink to match the SVG siblings' ink. Fusing
 * them back into one number reintroduces exactly this bug. (Round 1's constant
 * for the box was `EMOJI_TOOLBAR_BUTTON_PX`, which pinned the BUTTON's own
 * width; round 3 below replaces it with `EMOJI_TOOLBAR_ICON_BOX_PX` and stops
 * pinning the button at all, for reasons that are themselves measured.)
 *
 * `!important` is deliberate throughout: every rule here is overriding Froala's
 * bundled core stylesheet, not app CSS.
 *
 * ROUND 3 — THE INK IS THE WRONG COLOUR, AND HARDCODING IS THE PATTERN
 * (Gordon, 3rd report on this one toolbar, 2026-08-01: "Looks good, but the
 * color is BLACK and should be a more on-brand dark blue/gray or whatever is
 * standard on the app.")
 *
 * MEASURED live on web-stg (`main.64da3626.js`, 2026-08-01, 1440px,
 * `/builder/3169`), device-pixel sampled from the rendered glyphs, `bold`
 * carried as a known-good control:
 *
 *   emoticons         #000000   <- the complaint
 *   specialCharacters #565556   <- the app's standard toolbar icon colour
 *   aiAssistant       #0f52ba   <- a deliberate brand accent, not the standard
 *   bold (control)    #565556
 *
 * Rounds 1 and 2 could not have fixed this. The emoji was an `<img>`, and an
 * `<img>` paints the asset's own colours — no CSS `fill`, no `currentColor`, no
 * token can reach inside it. Its blackness was structural. So round 3 makes it
 * a REAL SVG icon, inline, with no `fill` attribute on its paths, exactly like
 * its siblings, and it takes its colour from the shared token in
 * `fieldToolbarIconColor.js`. That ends the class of defect instead of patching
 * a third symptom of it.
 *
 * THE SAME PASS CAUGHT A FOURTH MISMATCH FORMING, which is the real lesson.
 * Round 1 hardcoded `EMOJI_TOOLBAR_BUTTON_PX = 24` to put this button on what
 * was then the row's grid. The app has since moved every toolbar button to a
 * 32px touch target (`index.scss`, `button.fr-command.fr-btn`), and because
 * this one button pinned its own width with `!important`, it did not follow —
 * MEASURED 2026-08-01: emoticons 24x32, every sibling 32x32, and the visual
 * ink-to-ink gaps consequently uneven at 16.75px vs 22px. A hardcoded copy of a
 * shared value drifts the moment the shared value moves. That is the same fault
 * as the colour, in a different property.
 *
 * So the button width pin is DELETED rather than re-pinned to 32: the button
 * now inherits `min-width` from the same `index.scss` rule that sizes every
 * sibling, and a future touch-target change carries it along automatically.
 * Only the two things that genuinely cannot be inherited stay explicit — the
 * ICON BOX (because Froala's text-template wrapper has no box of its own) and
 * the GLYPH size (because this asset paints 94% of its box where a Froala SVG
 * paints ~58-73% of its own, which was round 2).
 *
 * ROUND 4 — THE FIX ONLY REACHED ONE OF THE BUTTON'S TWO HOMES
 * (gordon-0915-froala-toolbar-kebab-ur-emoji-clipped-bullet-missing, Gordon
 * screenshot 2026-09-15, web-dev /builder/4908, Wheel Settings > Description):
 * "the emoji button is half clipped off the bottom of the toolbar."
 *
 * MEASURED live (web-dev.iyuga.net/builder/6346, Description field, 1440px):
 * with the field's `moreMisc` overflow ("more"/kebab) panel open, the SAME
 * `emoticons` button that renders correctly in the main row renders with its
 * bottom ~7px cropped in the overflow panel — the exact round-1 symptom,
 * reappearing. Root cause: Froala renders the main row's buttons as children
 * of a `.fr-btn-grp` container, but renders the OVERFLOW panel's buttons as
 * DIRECT children of `.fr-more-toolbar` instead (confirmed via the live DOM:
 * `button.fr-command[data-cmd="emoticons"]`'s parent is
 * `.fr-more-toolbar.position-relative.fr-expanded`, not a `.fr-btn-grp`).
 * Rounds 1-3's selectors all required `.fr-btn-grp > button...`, so none of
 * them ever matched this button when Froala placed it in the panel instead of
 * the row, and it silently fell back to Froala's unfixed core styles (the
 * 17px `overflow:hidden` line box round 1 already diagnosed).
 *
 * FIX: drop the `.fr-btn-grp >` requirement from every selector below. The
 * button carries a unique `data-cmd="${EMOJI_TOOLBAR_CMD}"]`, so a plain
 * `.fr-toolbar button.fr-command[...]` descendant selector matches the same
 * one button in EITHER home without needing to enumerate Froala's internal
 * container structure, and stays correct if a future Froala version adds a
 * third.
 */

import { FIELD_TOOLBAR_ICON_COLOR_CSS_VALUE } from "./fieldToolbarIconColor";
import { SMILEY_ICON_PATHS, SMILEY_ICON_VIEWBOX } from "./smileyIconPaths";

/**
 * The sibling icon's ELEMENT box, in px — the size `index.scss` gives every
 * `button.fr-command.fr-btn > svg`, and therefore the box the emoji's wrapper
 * must occupy so the smiley sits on the same rhythm.
 *
 * MEASURED 2026-08-01: a sibling's 24px `<svg>` sits at its 32px button's
 * LEADING edge, not centred in it — so its ink centres on `button.x + 12`.
 * Giving the emoji's wrapper this same 24px leading box (rather than centring
 * it in the full 32px button) is what puts its ink on the same centre. Centring
 * in the button instead would push the smiley 4px right of every other icon.
 *
 * This replaces round 1's `EMOJI_TOOLBAR_BUTTON_PX`, which pinned the BUTTON
 * width and went stale when the app's touch target moved 24px -> 32px. The
 * button width is no longer stated here at all; it is inherited.
 */
export const EMOJI_TOOLBAR_ICON_BOX_PX = 24;

/**
 * The size to render the smiley GLYPH at, in px — deliberately smaller than
 * `EMOJI_TOOLBAR_ICON_BOX_PX`, because the sibling SVG icons are also 24x24
 * elements that paint far less than 24px of ink inside themselves.
 *
 * `black-smiley.svg` is a `0 0 17 17` viewBox whose outer circle spans
 * x/y 0.305..16.331, so it paints `16.026/17 = 94.3%` of whatever box it is
 * given — very nearly edge to edge, unlike its siblings.
 *
 * HOW 15 WAS CHOSEN (both numbers stated so the call is auditable):
 *   - GEOMETRIC answer: the siblings' ink heights are 14.01 and 16.00, mean
 *     15.00. A 16px image paints 16 * 0.943 = 15.08px of ink — a 0.08px match
 *     to that mean, and arithmetically the "correct" answer.
 *   - OPTICAL answer, which is what shipped: 15px, painting 14.14px of ink.
 *     A closed circle reads larger than an open letterform at equal bounding
 *     height, because it encloses its whole box while an omega or a sparkle
 *     does not. Measured against the trio at 4x, 16px still read a touch large;
 *     15px lands the smiley's ink on the omega's (14.14 vs 14.01) and sits at
 *     the low end of the band the two siblings already span, which is the right
 *     side to err on for an icon reported twice as too big.
 */
export const EMOJI_TOOLBAR_GLYPH_PX = 15;

/**
 * Fraction of its box that `black-smiley.svg` actually paints, from the asset's
 * own path extents (`0 0 17 17` viewBox, circle spanning 0.305..16.331).
 * Exported so a test can assert the resulting ink without re-deriving it.
 */
export const EMOJI_ASSET_INK_RATIO = 16.026 / 17;

/** The ink the glyph paints at `EMOJI_TOOLBAR_GLYPH_PX`, in css px. */
export const EMOJI_TOOLBAR_GLYPH_INK_PX =
  EMOJI_TOOLBAR_GLYPH_PX * EMOJI_ASSET_INK_RATIO;

/** `data-cmd` Froala stamps on the emoji button. */
export const EMOJI_TOOLBAR_CMD = "emoticons";

/**
 * Markup for the emoji toolbar icon, fed to Froala's `DefineIcon` `NAME`.
 *
 * An INLINE `<svg>`, not an `<img>` — that is round 3's fix. An `<img>` paints
 * the asset's own colours and is unreachable by CSS, which is why this icon was
 * `#000000` while every sibling was the toolbar's `#565556`. Inline paths with
 * NO `fill` attribute take the colour token from the stylesheet below, exactly
 * as the sibling Froala SVGs take theirs from `index.scss`.
 *
 * Sized to `EMOJI_TOOLBAR_GLYPH_PX` — the INK-matched size from round 2, not
 * the icon box — so the smiley reads the same size as the icons beside it, and
 * `display:block` so it never sits on a text baseline (the baseline is what put
 * the old image half outside its line box in round 1).
 *
 * A SIZED SVG NEEDS `flex: 0 0 auto` IN THIS WRAPPER, and that is not
 * decoration. MEASURED 2026-08-01 on live staging: with the wrapper's
 * `display: flex`, this glyph renders 10px wide — its `1em` — even though both
 * the attribute and the inline style say 15px. An `<img>` was immune because it
 * has an intrinsic width, so the flex `min-width: auto` floor protected it; an
 * inline `<svg>` has none, so the flex line shrinks it. Setting
 * `flex-shrink: 0`, `flex: 0 0 auto`, or an explicit `min-width` each restored
 * 15px in the same measurement, and `flex: 0 0 auto` is the one that says why.
 * The declaration lives in the stylesheet below. This was worth chasing rather
 * than papering over with an inline `!important`, which also worked and would
 * have left the next reader with no idea what it was fighting.
 *
 * No `alt`, and `aria-hidden`, because the icon is decorative here: Froala
 * already gives the button `title="Insert Emoji"` and an `.fr-sr-only` label,
 * so naming the glyph too would double-announce it.
 *
 * @returns {string} an inline `<svg>` tag
 */
export function buildEmojiToolbarIconMarkup() {
  return (
    `<svg viewBox="${SMILEY_ICON_VIEWBOX}" ` +
    `width="${EMOJI_TOOLBAR_GLYPH_PX}" height="${EMOJI_TOOLBAR_GLYPH_PX}" ` +
    `aria-hidden="true" focusable="false" ` +
    `style="display:block;width:${EMOJI_TOOLBAR_GLYPH_PX}px;height:${EMOJI_TOOLBAR_GLYPH_PX}px;">` +
    SMILEY_ICON_PATHS.map((d) => `<path d="${d}"></path>`).join("") +
    `</svg>`
  );
}

/**
 * Stylesheet injected once into `<head>` by FroalaTextEditor. Applies on every
 * surface that renders the shared editor, so the fix cannot be one-caller deep.
 */
export const EMOJI_TOOLBAR_BUTTON_CSS = `
        .fr-toolbar button.fr-command[data-cmd="${EMOJI_TOOLBAR_CMD}"] {
          /* NO width pin. Round 1 hardcoded 24px here to join what was then the
             row's grid; the app has since moved every toolbar button to a 32px
             touch target and this button, alone, did not follow — MEASURED
             2026-08-01: emoticons 24x32 against every sibling's 32x32. Froala
             sizes a text-template button to its content, which is what made
             this button 46px wide originally, but the wrapper below is now a
             fixed ${EMOJI_TOOLBAR_ICON_BOX_PX}px box, so the button falls back
             to the same \`min-width\` index.scss gives every sibling and tracks
             it automatically the next time that value moves.
             Only the padding still needs overriding. */
          padding: 0 !important;
        }
        .fr-toolbar button.fr-command[data-cmd="${EMOJI_TOOLBAR_CMD}"] > span:not(.fr-sr-only) {
          /* Froala core gives this span line-height:17px + overflow:hidden, which
             cropped the bottom off the icon. Centre the glyph in a box of its
             own instead of hanging it off a text baseline. */
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          /* the SIBLING ICON BOX, not the button. MEASURED 2026-08-01: a
             sibling's 24px <svg> sits at its 32px button's LEADING edge, so its
             ink centres on button.x + 12. Matching that box puts the smiley on
             the same centre; stretching this to 100% of the 32px button would
             push it 4px right of every other icon in the row. */
          width: ${EMOJI_TOOLBAR_ICON_BOX_PX}px !important;
          height: 100% !important;
          line-height: 0 !important;
          overflow: visible !important;
          /* Froala core puts 0 4px padding on this span, which pushed the icon
             3px right of the button box and left the emoji->omega gap 3px short
             of the omega->sparkle one even after the button width was pinned. */
          padding: 0 !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          /* Froala core also floors this span at min-width:30px. Left in place it
             holds the wrapper 6px wider than the button and centres the icon 3px
             off, which is exactly the residual gap difference measured after the
             button width alone was pinned. */
          min-width: 0 !important;
          max-width: none !important;
        }
        .fr-toolbar button.fr-command[data-cmd="${EMOJI_TOOLBAR_CMD}"] > span:not(.fr-sr-only) > svg {
          /* ROUND 2's INK FIX, unchanged in effect. Sized to the GLYPH constant,
             not the icon box: a Froala SVG sibling is also a 24x24 element but
             paints only ~13-18px of ink inside its viewBox, so an emoji filling
             its whole box read visibly larger than every icon next to it. Only
             the ink shrinks; the box above keeps the gaps even. */
          display: block !important;
          /* MEASURED 2026-08-01: without this the flex wrapper above shrinks the
             glyph to 10px — its 1em — because an inline <svg> has no intrinsic
             width for the flex min-width:auto floor to protect. The <img>
             this replaced did have one, which is why round 2 never met this. */
          flex: 0 0 auto !important;
          width: ${EMOJI_TOOLBAR_GLYPH_PX}px !important;
          height: ${EMOJI_TOOLBAR_GLYPH_PX}px !important;
        }
        .fr-toolbar button.fr-command[data-cmd="${EMOJI_TOOLBAR_CMD}"] > span:not(.fr-sr-only) > svg path {
          /* ROUND 3's COLOUR FIX. The token, never a literal — the same custom
             property index.scss hands the sibling SVGs. MEASURED before this
             change: this glyph #000000 against the siblings' #565556. It was an
             <img> then, so no rule of any kind could reach its pixels; it is an
             inline <svg> now, so this one declaration does. A palette change
             moves all three together and cannot move one without the others. */
          fill: ${FIELD_TOOLBAR_ICON_COLOR_CSS_VALUE} !important;
        }
        .fr-command[data-cmd="${EMOJI_TOOLBAR_CMD}"]:hover {
          transform: scale(1.1);
          transition: transform 0.2s ease;
        }
        @media (prefers-reduced-motion: reduce) {
          .fr-command[data-cmd="${EMOJI_TOOLBAR_CMD}"]:hover {
            transform: none;
            transition: none;
          }
        }
`;
